import { Suspense, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'

import ChatWidget from './ChatWidget'
import Footer from './Footer'
import Navbar from './Navbar'
import SeoHead from './SeoHead'
import { initialLanguage, isSupportedLanguage, storeLanguage } from '../i18n'

/**
 * Wraps every page. Reads the :lang route param, keeps i18n / <html lang> /
 * localStorage in sync with it, and redirects to a valid language when the
 * prefix is missing or unknown (e.g. someone opens /about directly).
 */
function LangLayout() {
  const { lang } = useParams()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const valid = !!lang && isSupportedLanguage(lang)

  useEffect(() => {
    if (!valid || !lang) return
    if (i18n.language !== lang) void i18n.changeLanguage(lang)
    storeLanguage(lang)
    document.documentElement.lang = lang
  }, [valid, lang, i18n])

  if (!valid) {
    return (
      <Navigate
        to={`/${initialLanguage()}${location.pathname}${location.search}${location.hash}`}
        replace
      />
    )
  }

  return (
    <div className="app-shell">
      {/* First focusable element on every page: hidden until it receives
          keyboard focus (.skip-link in index.css), then jumps straight to
          <main> instead of making a keyboard user tab through the utility
          bar and full nav first. tabIndex=-1 on <main> makes it a valid
          focus target (a plain landmark isn't focusable on its own), so
          focus - not just scroll position - actually lands there. */}
      <a href="#main-content" className="skip-link">
        {t('nav.skipToContent')}
      </a>
      <SeoHead />
      <Navbar />
      <main id="main-content" className="page-container" tabIndex={-1}>
        {/* Falls back to nothing while a lazily-loaded page chunk fetches -
            App.tsx's routes each resolve to a separate React.lazy import.
            .page-container is flex: 1 inside .app-shell's flex column, so
            it keeps filling the available height either way: no footer
            jump while the chunk loads. */}
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
      <ChatWidget />
    </div>
  )
}

export default LangLayout
